import React, { useState } from 'react';
import { ChevronDown, HelpCircle, Search } from 'lucide-react';
import { faqData } from '../mockData';
import CTA from '../components/CTA';

const FAQ = () => {
  const [openId, setOpenId] = useState(null);

  const toggleFAQ = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div>
      {/* Modern Hero Section - Green Theme */}
      <div className="relative bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900 py-40 mt-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'linear-gradient(to right, #f97316 1px, transparent 1px), linear-gradient(to bottom, #f97316 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-teal-500 rounded-full opacity-20 blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-3 bg-emerald-500/10 backdrop-blur-sm border border-emerald-500/20 rounded-full px-6 py-3 mb-8">
            <HelpCircle size={20} className="text-emerald-400" />
            <span className="text-emerald-400 font-bold text-sm uppercase tracking-wider">Help Center</span>
          </div>
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-none">
            <span className="block">Frequently</span>
            <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Asked</span>
            <span className="block">Questions</span>
          </h1>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions about our services
          </p>
        </div>
      </div>

      {/* FAQ Section - Modern Accordion */}
      <section className="py-32 bg-gray-50 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {faqData.map((faq, index) => {
              const isOpen = openId === faq.id;
              
              return (
                <div
                  key={faq.id}
                  className="group relative"
                >
                  {/* Modern Card Design */}
                  <div className={`bg-white rounded-2xl overflow-hidden shadow-lg border-2 transition-all duration-500 ${
                    isOpen 
                      ? 'border-emerald-500 shadow-2xl' 
                      : 'border-gray-100 hover:border-emerald-300 hover:shadow-xl'
                  }`}>
                    <button
                      onClick={() => toggleFAQ(faq.id)}
                      className="w-full flex items-center justify-between p-8 text-left hover:bg-emerald-50 transition-all duration-300"
                    >
                      {/* Question Number Badge */}
                      <div className="flex items-start space-x-4 flex-grow">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg transition-all duration-300 ${
                          isOpen 
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white' 
                            : 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200'
                        }`}>
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <span className={`text-xl font-bold pr-8 transition-colors duration-300 ${
                          isOpen ? 'text-emerald-600' : 'text-gray-900 group-hover:text-emerald-600'
                        }`}>
                          {faq.question}
                        </span>
                      </div>
                      
                      {/* Animated Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        isOpen 
                          ? 'bg-emerald-500 rotate-180' 
                          : 'bg-gray-100 group-hover:bg-emerald-100'
                      }`}>
                        <ChevronDown size={24} className={isOpen ? 'text-white' : 'text-gray-400 group-hover:text-emerald-600'} />
                      </div>
                    </button>
                    
                    {/* Answer with Smooth Transition */}
                    <div className={`overflow-hidden transition-all duration-500 ${
                      isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="px-8 pb-8 pl-24">
                        <div className="pt-4 border-t-2 border-emerald-100">
                          <p className="text-lg text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Help Section */}
          <div className="mt-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-12 shadow-2xl text-center text-white relative overflow-hidden">
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
                <Search size={32} className="text-white" />
              </div>
              <h3 className="text-4xl font-black mb-4">
                Still Have Questions?
              </h3>
              <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
                Our team is here to help. Get in touch with us for personalized assistance.
              </p>
              <a
                href="/contact"
                className="inline-block bg-white text-emerald-600 px-10 py-5 rounded-xl font-black text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Contact Our Team
              </a>
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default FAQ;
